import {
  Entity,
  generateFractionalIndexingKeyBetween,
  LiveData,
} from '@toeverything/infra';
import { map, of, switchMap } from 'rxjs';

import type { FolderStore } from '../stores/folder';

export function compareFolderIndex(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function generateIndexBefore(right: string | null, left: string | null) {
  if (left !== null && right !== null && compareFolderIndex(left, right) >= 0) {
    return generateFractionalIndexingKeyBetween(null, right);
  }
  return generateFractionalIndexingKeyBetween(left, right);
}

function generateIndexAfter(left: string | null, right: string | null) {
  if (left !== null && right !== null && compareFolderIndex(left, right) >= 0) {
    return generateFractionalIndexingKeyBetween(left, null);
  }
  return generateFractionalIndexingKeyBetween(left, right);
}

function nodeIndex(node: FolderNode | null | undefined): string | null {
  return node?.index$.value || null;
}

export class FolderNode extends Entity<{
  id: string | null;
}> {
  id = this.props.id;

  info$ = LiveData.from<{
    data: string;
    type: (string & {}) | 'folder' | 'doc';
    index: string;
    id: string;
    parentId?: string | null;
  } | null>(this.store.watchNodeInfo(this.id ?? ''), null);
  type$ = this.info$.map(info =>
    this.id === null ? 'folder' : (info?.type ?? '')
  );
  data$ = this.info$.map(info => info?.data);
  name$ = this.info$.map(info => (info?.type === 'folder' ? info.data : ''));
  children$ = LiveData.from<FolderNode[]>(
    // watch children if this is a folder, otherwise return empty array
    this.type$.pipe(
      switchMap(type =>
        type === 'folder'
          ? this.store
              .watchNodeChildren(this.id)
              .pipe(
                map(children =>
                  children
                    .filter(e => this.filterInvalidChildren(e))
                    .map(child =>
                      this.framework.createEntity(FolderNode, child)
                    )
                )
              )
              .pipe()
          : of([])
      )
    ),
    []
  );
  sortedChildren$ = LiveData.computed(get => {
    return get(this.children$)
      .map(node => [node, get(node.name$) ?? '', get(node.index$)] as const)
      .sort((a, b) => {
        // Manual sidebar order is encoded in the fractional index.
        // Name is only a stable fallback for malformed/equal indices.
        const indexCompare = compareFolderIndex(a[2], b[2]);
        if (indexCompare !== 0) return indexCompare;
        return a[1].localeCompare(b[1]);
      })
      .map(([node]) => node);
  });
  index$ = this.info$.map(info => info?.index ?? '');

  constructor(readonly store: FolderStore) {
    super();
  }

  contains(childId: string | null): boolean {
    if (!this.id) {
      return true;
    }
    if (!childId) {
      return false;
    }
    return this.store.isAncestor(childId, this.id);
  }

  beChildOf(parentId: string | null): boolean {
    if (!this.id) {
      return false;
    }
    if (!parentId) {
      return true;
    }
    return this.store.isAncestor(this.id, parentId);
  }

  filterInvalidChildren(child: { type: string }): boolean {
    if (this.id === null && child.type !== 'folder') {
      return false; // root node can only have folders
    }
    return true;
  }

  createFolder(name: string, index: string, id?: string) {
    if (this.type$.value !== 'folder') {
      throw new Error('Cannot create folder on non-folder node');
    }
    return this.store.createFolder(this.id, name, index, id);
  }

  createLink(type: 'doc', targetId: string, index: string) {
    if (this.id === null) {
      throw new Error('Cannot create link on root node');
    }
    if (this.type$.value !== 'folder') {
      throw new Error('Cannot create link on non-folder node');
    }
    return this.store.createLink(this.id, type, targetId, index);
  }

  delete() {
    if (this.id === null) {
      throw new Error('Cannot delete root node');
    }
    if (this.type$.value === 'folder') {
      this.store.removeFolder(this.id);
    } else {
      this.store.removeLink(this.id);
    }
  }

  moveHere(childId: string, index: string) {
    this.store.moveNode(childId, this.id, index);
  }

  rename(name: string) {
    if (this.id === null) {
      throw new Error('Cannot rename root node');
    }
    this.store.renameNode(this.id, name);
  }

  indexAt(at: 'before' | 'after', targetId?: string) {
    if (!targetId) {
      if (at === 'before') {
        const first = this.sortedChildren$.value.at(0);
        return generateIndexBefore(nodeIndex(first), null);
      } else {
        const last = this.sortedChildren$.value.at(-1);
        return generateIndexAfter(nodeIndex(last), null);
      }
    } else {
      const sortedChildren = this.sortedChildren$.value;
      const targetIndex = sortedChildren.findIndex(
        node => node.id === targetId
      );
      if (targetIndex === -1) {
        throw new Error('Target node not found');
      }
      const target = sortedChildren[targetIndex];
      const before: FolderNode | null = sortedChildren[targetIndex - 1] || null;
      const after: FolderNode | null = sortedChildren[targetIndex + 1] || null;
      if (at === 'before') {
        return generateIndexBefore(nodeIndex(target), nodeIndex(before));
      } else {
        return generateIndexAfter(nodeIndex(target), nodeIndex(after));
      }
    }
  }
}
